import { AddressAutocompleteInput } from '../common/AddressAutocompleteInput';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Textarea,
  Typography,
  Stack,
  IconButton,
  Alert,
  Divider,
} from '@mui/joy';
import { Plus as AddIcon, Trash as DeleteIcon } from '@phosphor-icons/react';
import { distributorsApi, type Distributor, type IntegrationType } from '../../lib/distributorsApi';
import DistributorConnectionCard from './DistributorConnectionCard';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

interface Props {
  distributor?: Distributor | null;
  onSave: (saved: Distributor, connectAccount: boolean) => void;
  onCancel: () => void;
  /** OAuth return outcome parsed by DistributorsApp (?connected= / ?error=). */
  oauthResult?: { connected: boolean; error?: string } | null;
  onOauthResultConsumed?: () => void;
}

interface Location {
  address: string;
  city: string;
  state: string;
  zip: string;
}

export default function DistributorForm({ distributor, onSave, onCancel, oauthResult, onOauthResultConsumed }: Props) {
  const liveOrdering = useFeatureFlag('abc_supply_integration');
  const [formData, setFormData] = useState({
    name: '',
    sales_rep_name: '',
    phone_number: '',
    default_order_email: '',
    notes: '',
    integration_type: 'email' as IntegrationType,
    locations: [{ address: '', city: '', state: '', zip: '' }] as Location[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (distributor) {
      setFormData({
        name: distributor.name,
        sales_rep_name: distributor.sales_rep_name,
        phone_number: distributor.phone_number,
        default_order_email: distributor.default_order_email,
        notes: distributor.notes || '',
        integration_type: (distributor.integration_type || 'email') as IntegrationType,
        locations: distributor.locations && distributor.locations.length > 0
          ? distributor.locations
          : [{ address: '', city: '', state: '', zip: '' }],
      });
    }
  }, [distributor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Filter out empty locations
      const validLocations = formData.locations ? formData.locations.filter(
        loc => loc.address || loc.city || loc.state || loc.zip
      ) : [];

      const dataToSave = {
        ...formData,
        locations: validLocations.length > 0 ? validLocations : [],
      };

      const saved = distributor
        ? await distributorsApi.update(distributor.id, dataToSave)
        : await distributorsApi.create(dataToSave);
      const connectAccount = liveOrdering && !!saved.integration_type && saved.integration_type !== 'email'
        && (!distributor || distributor.integration_type !== saved.integration_type);
      onSave(saved, connectAccount);
    } catch (err: any) {
      let errorMessage = 'Failed to save distributor';
      
      // Handle validation errors
      if (err.response?.data || err.responseData) {
        const data = err.response?.data || err.responseData;
        
        // Check for field-specific validation errors
        if (data.name) {
          errorMessage = Array.isArray(data.name) ? data.name[0] : data.name;
        } else if (data.phone_number) {
          errorMessage = Array.isArray(data.phone_number) ? data.phone_number[0] : data.phone_number;
        } else if (data.default_order_email) {
          errorMessage = Array.isArray(data.default_order_email) ? data.default_order_email[0] : data.default_order_email;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addLocation = () => {
    setFormData({
      ...formData,
      locations: [...(formData.locations || []), { address: '', city: '', state: '', zip: '' }],
    });
  };

  const removeLocation = (index: number) => {
    setFormData({
      ...formData,
      locations: (formData.locations || []).filter((_, i) => i !== index),
    });
  };

  const updateLocation = (index: number, field: keyof Location, value: string) => {
    const updatedLocations = [...(formData.locations || [])];
    updatedLocations[index] = { ...updatedLocations[index], [field]: value };
    setFormData({ ...formData, locations: updatedLocations });
  };

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <Box sx={{ p: 3 }}>
          <Typography level="h3" sx={{ mb: 3 }}>
            {distributor ? 'Edit Distributor' : 'Add New Distributor'}
          </Typography>

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Company Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Sales Representative</FormLabel>
              <Input
                value={formData.sales_rep_name}
                onChange={(e) => setFormData({ ...formData, sales_rep_name: e.target.value })}
                required
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Phone Number</FormLabel>
              <Input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                placeholder="+1234567890"
                required
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Order Email</FormLabel>
              <Input
                type="email"
                value={formData.default_order_email}
                onChange={(e) => setFormData({ ...formData, default_order_email: e.target.value })}
                required
              />
            </FormControl>

            <FormControl>
              <FormLabel>Integration Type</FormLabel>
              <Select
                value={formData.integration_type}
                onChange={(_event, value) => {
                  if (value) setFormData({ ...formData, integration_type: value as IntegrationType });
                }}
              >
                <Option value="email">Email (send PO by email)</Option>
                {/* Hidden rather than disabled when the org is not switched on:
                    unlike SRS and QXO this is a live product, so offering it
                    greyed out would invite a support ticket. A distributor
                    already set to it still shows its own value. */}
                {(liveOrdering || formData.integration_type === 'api_abc') && (
                  <Option value="api_abc" disabled={!liveOrdering}>
                    ABC Supply API (live pricing &amp; ordering)
                  </Option>
                )}
                {/*
                  SRS and QXO have provider implementations behind them, but the
                  vendor onboarding they need is not finished, so they are not
                  selectable yet. An existing distributor already set to one
                  still renders its own value.
                */}
                <Option value="api_srs" disabled={!liveOrdering}>
                  SRS Distribution API (live pricing &amp; ordering)
                </Option>
                <Option value="api_qxo" disabled={formData.integration_type !== 'api_qxo'}>
                  QXO / Beacon API (coming soon)
                </Option>
              </Select>
              <FormHelperText>
                API-integrated distributors get live catalog search, customer-specific
                pricing, and direct order placement with status tracking.
              </FormHelperText>
            </FormControl>

            {liveOrdering && formData.integration_type !== 'email' && (
              distributor?.id && distributor.integration_type === formData.integration_type ? (
                <DistributorConnectionCard
                  distributorId={distributor.id}
                  oauthResult={oauthResult}
                  onResultConsumed={onOauthResultConsumed}
                />
              ) : (
                <Alert color="primary" variant="soft">
                  Save the distributor to continue to account connection.
                </Alert>
              )
            )}

            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                minRows={3}
              />
            </FormControl>

            <Divider />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography level="title-md">Locations</Typography>
                <Button
                  size="sm"
                  startDecorator={<AddIcon />}
                  onClick={addLocation}
                  variant="outlined"
                  color="primary"
                >
                  Add Location
                </Button>
              </Box>

              {formData.locations && formData.locations.map((location, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography level="body-md">Location {index + 1}</Typography>
                    {formData.locations && formData.locations.length > 1 && (
                      <IconButton
                        size="sm"
                        color="danger"
                        onClick={() => removeLocation(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Stack spacing={2}>
                    <AddressAutocompleteInput
                      value={location.address}
                      onChange={(value) => updateLocation(index, 'address', value)}
                      onAddressSelect={(parsed) => setFormData(previous => ({ ...previous, locations: (previous.locations || []).map((item, i) => i === index ? { ...item, address: parsed.address_1, city: parsed.city, state: parsed.state, zip: parsed.zip_code } : item) }))}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 2 }}>
                      <Input
                        placeholder="City"
                        value={location.city}
                        onChange={(e) => updateLocation(index, 'city', e.target.value)}
                      />
                      <Input
                        placeholder="State"
                        value={location.state}
                        onChange={(e) => updateLocation(index, 'state', e.target.value)}
                      />
                      <Input
                        placeholder="ZIP"
                        value={location.zip}
                        onChange={(e) => updateLocation(index, 'zip', e.target.value)}
                      />
                    </Box>
                  </Stack>
                </Card>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button 
                variant="outlined" 
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                loading={loading}
              >
                {distributor ? 'Save Changes' : 'Create Distributor'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </form>
    </Card>
  );
}
